import { forwardRef, useCallback } from 'react';
import { useFormatDate } from '@/hooks/date';
import usePopoverState from '@/hooks/usePopoverState';
import { cn } from '@/lib/cn';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { formatISODate } from '@/utils/date';
import Icon from '../Icon/Icon';
import { HStack } from '../Layout';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar } from './Calendar';
import { resolveFromDate } from './resolveFromDate';
type OnChange = (value: Date | undefined) => void;
type Props = {
  id?: string;
  disabled?: boolean;
  name?: string;
  className?: string;
  value: Date | undefined;
  onChange?: OnChange;
  placeholder?: string;
  /**
   * トリガーの表示テキストを上書きする。期間ナビ（週次の範囲・月次の年月など）のように
   * value（選択日）とは別の文言を表示したい場合に使う。未指定なら value をフォーマットして表示。
   */
  displayLabel?: string;
  width?: number | string;
  disablePast?: boolean;
  disableToday?: boolean;
  showYearMonthSelect?: boolean;
  allowEmpty?: boolean;
  /** Minimum selectable date (inclusive) */
  fromDate?: Date;
  /** Maximum selectable date (inclusive) */
  toDate?: Date;
};
export const SimpleDatePicker = forwardRef<HTMLButtonElement, Props>(function SimpleDatePicker({
  id,
  disabled,
  name,
  className,
  value,
  onChange,
  placeholder,
  displayLabel,
  width,
  disablePast,
  disableToday,
  showYearMonthSelect = true,
  allowEmpty = false,
  fromDate: fromDateProp,
  toDate: toDateProp
}: Props, ref) {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'component.DatePicker'
  });
  const [open, setOpen] = usePopoverState();
  const formatDate = useFormatDate();
  const handleSelect = useCallback<OnChange>(date => setOpen(false).then(() => onChange?.(date)), [onChange, setOpen]);
  const resolvedFromDate = resolveFromDate({
    fromDate: fromDateProp,
    disablePast,
    disableToday
  });
  return <Popover open={open} onOpenChange={setOpen}>
        <HStack>
          <PopoverTrigger asChild>
            <Button ref={ref} id={id} disabled={disabled} name={name} type="button" variant="outline" value={value ? formatISODate(value) : ''} style={typeof width === 'number' ? {
          width
        } : undefined} className={cn('flex w-full min-w-0 justify-between bg-transparent px-3', className, typeof width === 'string' ? width : undefined)}>
              <Icon iconName="CalendarIcon" className="shrink-0" />
              {displayLabel ?? value ? <span className="flex-1 min-w-0 ml-2 truncate text-left">
                  {displayLabel ?? (value ? formatDate(value) : '')}
                </span> : <span className="flex-1 min-w-0 ml-2 truncate text-left text-muted-foreground">
                  {placeholder ?? t('placeholder')}
                </span>}
            </Button>
          </PopoverTrigger>
          {allowEmpty && value && !disabled && <Button type="button" variant="ghost" size="icon" onClick={() => onChange?.(undefined)}>
              <Icon iconName="XIcon" />
            </Button>}
        </HStack>
        <PopoverContent className="w-auto p-0">
          <Calendar mode="single" selected={value} onSelect={handleSelect} defaultMonth={value} showYearMonthSelect={showYearMonthSelect} fromDate={resolvedFromDate} toDate={toDateProp} />
        </PopoverContent>
      </Popover>;
});